import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import {
  BadgeCheck,
  BookOpenCheck,
  Check,
  FileText,
  ShieldCheck,
} from "lucide-react";
import axiosInstance from "../../axiosInstance";
import { EmptyState, ErrorState } from "../../components/ui/Feedback";
import "./Policy.scss";

const API = process.env.REACT_APP_API_EMPLOYEE_URL;

const getPolicyParagraphs = (info) => {
  if (Array.isArray(info)) return info.map(String).filter(Boolean);
  return String(info || "")
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
};

function Policy() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchTermsAndConditions = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${API}/terms-and-conditions/All`,
      );
      setPolicies(Array.isArray(response.data?.data) ? response.data.data : []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching policies:", err);
      setError("Failed to load terms and conditions.");
      setLoading(false);
    }
  }, []);

  const checkTermsStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${API}/employee/termsAndCondition`,
      );
      setTermsAccepted(response.data?.data === true);
    } catch (err) {
      console.error("Error checking terms status:", err);
    }
    await fetchTermsAndConditions();
  }, [fetchTermsAndConditions]);

  useEffect(() => {
    checkTermsStatus();
  }, [checkTermsStatus]);

  const handleAcceptTerms = async () => {
    if (!accepted) {
      toast.warning(
        "Please accept the terms and conditions before proceeding.",
      );
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.put(`${API}/employee/accept/termsAndCondition`);
      toast.success("Terms and conditions accepted successfully!");
      setTermsAccepted(true);
    } catch (err) {
      console.error("Error accepting terms:", err);
      toast.error("Failed to accept terms. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    checkTermsStatus();
  };

  const sectionCount = policies.length;
  const readingMinutes = useMemo(() => {
    const words = policies.reduce(
      (total, policy) =>
        total +
        getPolicyParagraphs(policy.info).join(" ").split(/\s+/).filter(Boolean)
          .length,
      0,
    );
    return Math.max(1, Math.ceil(words / 220));
  }, [policies]);

  return (
    <div className="policy-page">
      <div className="policy-overview" aria-label="Document details">
        <div className="policy-overview__item">
          <span className="policy-overview__icon">
            <BookOpenCheck size={17} />
          </span>
          <span>
            <strong>{sectionCount}</strong>{" "}
            {sectionCount === 1 ? "section" : "sections"}
          </span>
        </div>
        <span className="policy-overview__divider" aria-hidden="true" />
        <div className="policy-overview__item">
          <span className="policy-overview__icon">
            <FileText size={17} />
          </span>
          <span>About {readingMinutes} min read</span>
        </div>
        {!termsAccepted && sectionCount > 0 && (
          <span className="policy-overview__note">
            Please review every section before accepting.
          </span>
        )}
      </div>
      {loading ? (
        <div
          className="policy-sections"
          aria-busy="true"
          aria-label="Loading policies"
        >
          {Array.from({ length: 3 }, (_, index) => (
            <section
              key={index}
              className="hr-card policy-section policy-section--loading"
            >
              <span className="hr-skeleton policy-skeleton policy-skeleton--short" />
              <span className="hr-skeleton policy-skeleton" />
              <span className="hr-skeleton policy-skeleton" />
              <span className="hr-skeleton policy-skeleton policy-skeleton--medium" />
            </section>
          ))}
        </div>
      ) : error ? (
        <section className="hr-card policy-feedback">
          <ErrorState
            title="Unable to load terms"
            message={error}
            onRetry={handleRetry}
          />
        </section>
      ) : policies.length === 0 ? (
        <section className="hr-card policy-feedback">
          <EmptyState
            icon={<FileText size={22} />}
            title="No policies available"
            description="Terms and conditions will appear here once they are published."
          />
        </section>
      ) : (
        <>
          <div className="policy-sections" aria-label="Terms and conditions">
            {policies.map((policy, index) => {
              const paragraphs = getPolicyParagraphs(policy.info);
              return (
                <article
                  className="hr-card policy-section"
                  key={policy.id || index}
                >
                  <div className="policy-section__index" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <div className="policy-section__content">
                    <div className="policy-section__meta">
                      <span>Section {String(index + 1).padStart(2, "0")}</span>
                      <span
                        className="policy-section__dot"
                        aria-hidden="true"
                      />
                      <span>Company policy</span>
                    </div>
                    <div className="policy-section__body">
                      {paragraphs.map((paragraph, paragraphIndex) => (
                        <p key={paragraphIndex}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {!termsAccepted ? (
            <section
              className="hr-card policy-accept"
              aria-label="Accept terms"
            >
              <label className="policy-check">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={accepted}
                  onChange={(event) => setAccepted(event.target.checked)}
                />
                <span className="policy-check__text">
                  <strong>I have read and agree to these terms</strong>
                  <span>
                    I acknowledge that I have read, understood, and agree to be
                    bound by the company policies above.
                  </span>
                </span>
              </label>
              <button
                type="button"
                className="hr-btn hr-btn--primary hr-btn--lg policy-accept__button"
                onClick={handleAcceptTerms}
                disabled={!accepted || submitting}
              >
                {submitting ? (
                  "Submitting…"
                ) : (
                  <>
                    <Check size={16} aria-hidden="true" /> Accept terms
                  </>
                )}
              </button>
            </section>
          ) : (
            <section className="hr-card policy-confirmed" role="status">
              <span className="policy-confirmed__icon">
                <BadgeCheck size={19} aria-hidden="true" />
              </span>
              <span>
                <strong>You're all set</strong>
                <small>
                  You have accepted the current terms and conditions.
                </small>
              </span>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default Policy;
